import { Component, Inject, OnInit } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { SnackBarTypeEnum } from '../../../models/snackbar';

/**
 * Interface for snackbar data structure.
 */
interface SnackBarData {
  type?: number;
  message?: string;
}

/**
 * SnackbarComponent: Displays custom toast notifications with different types.
 * Supports multiple message types (Info, Success, Failure, Warning, Error, Reminder)
 * with customizable messages and default fallback messages.
 */
@Component({
  selector: 'app-snackbar',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './snackbar.component.html',
  styleUrl: './snackbar.component.scss'
})
export class SnackbarComponent implements OnInit {
  /** Message text to display in snackbar */
  message: string = '';

  /** Type of snackbar (Info, Success, Failure, etc.) */
  type: SnackBarTypeEnum = SnackBarTypeEnum.Info;

  /** CSS class name for snackbar status styling */
  snackBarStatus: string = '';

  /** Injected data containing type and message */
  data: SnackBarData = {};

  /**
   * Default messages for each snackbar type.
   * Used as fallback when custom message is not provided.
   */
  private readonly defaultMessages = {
    [SnackBarTypeEnum.Info]: 'Information: Please be aware of this.',
    [SnackBarTypeEnum.Success]: 'Operation completed successfully!',
    [SnackBarTypeEnum.Failure]: 'Operation failed. Please try again.',
    [SnackBarTypeEnum.Reminder]: 'Reminder: Please take note of this.',
    [SnackBarTypeEnum.Warning]: 'Warning! Please check your input.',
    [SnackBarTypeEnum.Error]: 'An error occurred. Please try again.',
  };

  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public injectedData: SnackBarData,
    private snackBarRef: MatSnackBarRef<SnackbarComponent>
  ) {
    try {
      this.data = this.isEmpty(injectedData) ? {} : injectedData;
    } catch (error) {
      console.error('Error in SnackbarComponent constructor:', error);
      this.data = {};
    }
  }

  /**
   * Angular lifecycle hook called after component initialization.
   * Initializes snackbar type, message, and status.
   */
  ngOnInit(): void {
    try {
      this.initializeSnackbar();
    } catch (error) {
      console.error('Error in ngOnInit:', error);
    }
  }

  /**
   * Initializes snackbar properties based on injected data.
   * Sets type, message with fallback to defaults, and status.
   */
  private initializeSnackbar(): void {
    this.type = this.isValidType(this.data?.type)
      ? (this.data.type as SnackBarTypeEnum)
      : SnackBarTypeEnum.Info;

    this.message = !this.isEmpty(this.data?.message)
      ? (this.data.message ?? '')
      : this.getDefaultMessage(this.type);

    this.snackBarStatus = this.getEnumKeyForValue(this.type) + ' !';
  }

  /**
   * Retrieves the default message for a given snackbar type.
   * @param type Snackbar type enum value
   * @returns Default message string for the type, or generic message if type is invalid
   */
  private getDefaultMessage(type: SnackBarTypeEnum): string {
    if (this.isEmpty(type)) {
      return 'Please note this notification.';
    }
    return this.defaultMessages[type] ?? 'Please note this notification.';
  }

  /**
   * Validates if a value is a valid SnackBarTypeEnum type.
   * @param value Value to validate
   * @returns true if value is a valid enum type, false otherwise
   */
  private isValidType(value: any): boolean {
    if (this.isEmpty(value) || typeof value !== 'number') {
      return false;
    }
    return Object.values(SnackBarTypeEnum).includes(value);
  }

  /**
   * Converts numeric type value to its corresponding enum key name.
   * @param value Numeric type value
   * @returns Enum key name (e.g., 'Success', 'Error', 'Info')
   */
  getEnumKeyForValue(value: number): string {
    try {
      if (this.isEmpty(value) || typeof value !== 'number') {
        return 'Info';
      }
      const key = SnackBarTypeEnum[value];
      return !this.isEmpty(key) ? key : 'Info';
    } catch (error) {
      console.error('Error getting enum key:', error);
      return 'Info';
    }
  }

  /**
   * Dismisses the snackbar notification.
   * Closes the snackbar reference without returning data.
   */
  dismiss(): void {
    try {
      if (!this.isEmpty(this.snackBarRef)) {
        this.snackBarRef.dismiss();
      }
    } catch (error) {
      console.error('Error dismissing snackbar:', error);
    }
  }

  /**
   * Checks if a value is empty (null, undefined, empty string, etc.).
   * Handles multiple data types: null, undefined, string, array, object, number.
   * @param value Value to check
   * @returns true if value is empty, false otherwise
   */
  private isEmpty(value: any): boolean {
    if (value == null) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }
    return false;
  }
}
